import WidgetModel, { SendDataConfig } from '@mm/state/model/WidgetModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import { StoreState } from '@mm/state/store-interfaces';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useMailchimpConnectionCRUDActions } from '@mm/state/actions/MailchimpConnectionsCRUDActions';
import { MAX_PAGE_SIZE } from '@mm/state/model/PageModel';
import {
  getMailchimpConnections,
  getWidgetKindMailchimpConnections,
} from '@mm/state/modules/mailchimpConnectionsTableState/selectors';
import { useMailchimpCRUDActions } from '@mm/state/actions/MailchimpCRUDActions';
import ConnectionModel, {
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import { ReactComponent as MailchimpIcon } from 'assets/mailchimp.svg';
import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Handle, Position, NodeProps } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import common from '../index.module.css';
import css from './MailChimp.module.css';
import { useConnectionWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { Count } from '../elements/Count/Count';
import { useOpenOnReady } from '../use-open-on-ready';

type Props = { className?: string } & NodeProps<{
  widget: WidgetModel;
}>;

export const MailChimp: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
}) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;
  const config = widget.config as Partial<SendDataConfig>;
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { retrieveAll, create } = useMailchimpConnectionCRUDActions();
  const { getAccounts, resetMailchimpStore } = useMailchimpCRUDActions();
  const { connectionWebhookId } = config;
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const viewActions = useViewActions();
  const connections = useSelector(getMailchimpConnections);

  useEffect(() => {
    resetMailchimpStore();
    if (activeFbPageId) {
      retrieveAll(activeFbPageId, {
        type: 'mailchimp',
        pageSize: MAX_PAGE_SIZE,
      });
    }
    getAccounts(activeFbPageId);
  }, [activeFbPageId]);

  const connection = connections.find(c => c.id === connectionWebhookId);

  const onConnectionChange = (connection: ConnectionModel) => {
    const { sendDataWidgetConfig: config } = connection;
    if (config) {
      const clonedWidget = {
        ...widget,
        config: {
          ...config,
          kind: 'mailchimp' as any,
        },
      };
      connectionWidgetHandlers.update(clonedWidget);
    }
  };

  const onEditConnection = () => {
    viewActions.showSlideoutMenu('mailchimpIntegrationEditor', {
      widgetId: data.widget.id,
    });
  };

  const onAddNewConnection = async () => {
    const newConnection = await create(activeFbPageId, {
      name: 'Mailchimp Connection',
      kind: ConnectionKind.WIDGET,
      description: '',
      connectionKind: 'mailchimp',
    });
    await retrieveAll(activeFbPageId, {
      type: 'mailchimp',
      pageSize: MAX_PAGE_SIZE,
    });

    if (newConnection) {
      viewActions.showSlideoutMenu('mailchimpIntegrationEditor', {
        widgetId: data.widget.id,
      });
      onConnectionChange(newConnection);
    }
  };

  const onClick = () => {
    if (isDragging) {
      return;
    }
    if (connections.length) {
      viewActions.showSlideoutMenu('connectionsEditor', {
        widgetId: data.widget.id,
        title: 'MailChimp Integration',
        selector: getWidgetKindMailchimpConnections,
        onConnectionChange,
        onAddNewConnection,
        onEditConnection,
      });
    } else {
      viewActions.showSlideoutMenu('mailchimpIntegrationEditor', {
        widgetId: data.widget.id,
      });
    }
  };

  useOpenOnReady(widget, onClick);

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div
        className={classNames(className, common.card, css.container)}
        onClick={onClick}
      >
        <div className={css.body}>
          <div className={css.header}>
            <div className={css.icon}>
              <MailchimpIcon />
            </div>

            {children}

            <span className={css.placeholder}>MailChimp Integration</span>
          </div>
          {connection && (
            <div className={css.info}>
              <span className={css.connectionName}>{connection.name}</span>

              <Count
                className={css.firesCount}
                value={`${data.widget.config?.firesCount || 0} contacts`}
              />
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
};
